import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Check, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { getAuthUrl } from "@/utils/redirectUtils";
import { motion } from "framer-motion";

const PricingSummarySection = () => {
  return (
    <section className="py-24 px-4 bg-background">
      <div className="container mx-auto max-w-5xl">
        <div className="text-center mb-16">
          <Badge className="px-3 py-1 text-sm bg-primary/10 text-primary border-primary/20 mb-4">
            Pricing
          </Badge>
          <h2 className="text-3xl md:text-5xl font-bold text-foreground mb-6">
            Pick your tier. Skip the agency.
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Three tiers, monthly billing, 7-day free trial on every tier.
            First 50 customers get founder pricing.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {/* Solo */}
          <motion.div
            className="bg-card rounded-xl border border-border p-8 flex flex-col"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4 }}
          >
            <h3 className="text-lg font-semibold text-foreground mb-1">Solo</h3>
            <div className="text-4xl font-bold text-foreground mb-1">
              $99<span className="text-lg font-normal text-muted-foreground">/mo</span>
            </div>
            <p className="text-xs text-muted-foreground mb-4">or $990/yr — save ~17%</p>
            <p className="text-sm text-muted-foreground mb-6">
              Solo founders running their own product.
            </p>
            <ul className="space-y-3 mb-8 flex-grow">
              {["1 brand", "Unlimited campaigns", "Unlimited reports", "All features"].map(
                (f) => (
                  <li key={f} className="flex items-center gap-2 text-sm text-foreground">
                    <Check className="h-4 w-4 text-primary flex-shrink-0" />
                    {f}
                  </li>
                )
              )}
            </ul>
            <Button
              className="w-full"
              variant="outline"
              onClick={() => window.location.href = getAuthUrl()}
            >
              Start free trial
            </Button>
          </motion.div>

          {/* Agency — featured */}
          <motion.div
            className="bg-card rounded-xl border-2 border-primary p-8 relative flex flex-col"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: 0.1 }}
          >
            <span className="absolute -top-3 left-6 px-3 py-0.5 bg-primary text-primary-foreground text-xs font-medium rounded-full">
              Most Popular
            </span>
            <h3 className="text-lg font-semibold text-foreground mb-1">Agency</h3>
            <div className="text-4xl font-bold text-foreground mb-1">
              $249<span className="text-lg font-normal text-muted-foreground">/mo</span>
            </div>
            <p className="text-xs text-muted-foreground mb-4">or $2,490/yr — save ~17%</p>
            <p className="text-sm text-muted-foreground mb-6">
              Freelancers managing client work.
            </p>
            <ul className="space-y-3 mb-8 flex-grow">
              {[
                "5 brands",
                "Unlimited campaigns",
                "Multi-account / MCC support",
                "Per-brand value-prop docs",
              ].map((f) => (
                <li key={f} className="flex items-center gap-2 text-sm text-foreground">
                  <Check className="h-4 w-4 text-primary flex-shrink-0" />
                  {f}
                </li>
              ))}
            </ul>
            <Button
              className="w-full"
              onClick={() => window.location.href = getAuthUrl()}
            >
              Start free trial <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </motion.div>

          {/* Studio */}
          <motion.div
            className="bg-card rounded-xl border border-border p-8 flex flex-col"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: 0.2 }}
          >
            <h3 className="text-lg font-semibold text-foreground mb-1">Studio</h3>
            <div className="text-4xl font-bold text-foreground mb-1">
              $599<span className="text-lg font-normal text-muted-foreground">/mo</span>
            </div>
            <p className="text-xs text-muted-foreground mb-4">or $5,990/yr — save ~17%</p>
            <p className="text-sm text-muted-foreground mb-6">
              Small agencies running 10–25 clients.
            </p>
            <ul className="space-y-3 mb-8 flex-grow">
              {[
                "25 brands",
                "Unlimited campaigns",
                "Priority founder support",
                "Cross-client roll-up reporting",
              ].map((f) => (
                <li key={f} className="flex items-center gap-2 text-sm text-foreground">
                  <Check className="h-4 w-4 text-primary flex-shrink-0" />
                  {f}
                </li>
              ))}
            </ul>
            <Button
              className="w-full"
              variant="outline"
              onClick={() => window.location.href = getAuthUrl()}
            >
              Start free trial
            </Button>
          </motion.div>
        </div>

        <div className="text-center mt-8">
          <Link
            to="/pricing"
            className="text-sm text-primary hover:underline font-medium"
          >
            View all plans & compare features →
          </Link>
        </div>
      </div>
    </section>
  );
};

export default PricingSummarySection;
